import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useMediaQuery } from '@mui/material';
import { baseUrl } from 'config';
import Head from 'next/head';
import { useRouter } from 'next/router';

import useAuthentication from '@/components/Authentication/useAuthentication';
import B2BBanner from '@/components/B2BBanner/B2BBanner';
import PaddingContainer from '@/components/PaddingContainer/PaddingContainer';
import FiltersSection from '@/components/Search/components/Filters/FiltersContainers';
import SearchHeader from '@/components/Search/components/SearchHeader/SearchHeader';
import SearchSeoText from '@/components/Search/components/SearchSeoText/SearchSeoText';
import SortSection from '@/components/Search/components/SortSection/SortSection';
import useFetchAnnounces from '@/components/Search/hooks/useFetchAnnounces';
import useFormattedSortMessages from '@/components/Search/hooks/useFormattedSortMessages';
import useSearchFilters from '@/components/Search/hooks/useSearchFilters';
import useSearchSort from '@/components/Search/hooks/useSearchSort';
import { SearchResultGridItems } from '@/components/SearchResultGridItems/SearchResultGridItems';
import { Button } from '@/designSystem/Button/Button';
import { useQueryImmutable } from '@/hooks/useQueryImmutable';
import { apiGetSubjects } from '@/shared/API/apiGetSubjects';
import DCEDiscountedPromoBanner from '@/shared/PromoBanner/DceDiscountedPromoBanner/DCEDiscountedPromoBanner';
import SponsoringPromoBanner from '@/shared/PromoBanner/SponsoringPromoBanner/SponsoringPromoBanner';
import handleSendEventGTM from '@/utils/googleTagManagerHelper';
import { useIsMobile } from '@/utils/useResponsive';

import messages from './messages';
import { SearchDefaultProps } from './types';

import useStyles, { SEARCH_BREAKPOINT } from './styles';

const Search: FC<SearchDefaultProps> = ({ defaultSubject, defaultAddress }) => {
  const { formattedSortCriteria } = useFormattedSortMessages();
  const { authentication } = useAuthentication();
  const { data: subjects } = useQueryImmutable(['subject'], apiGetSubjects);
  const { classes } = useStyles();
  const isStickyFilters = !useMediaQuery(SEARCH_BREAKPOINT);
  const isMobile = useIsMobile();
  const { query } = useRouter();
  const isPageWithParams = Object.keys(query).length > 0;

  const {
    subject,
    setSubject,
    address,
    setAddress,
    sidePanelFilters,
    setSidePanelFilters,
    debounce: debounceFilters,
  } = useSearchFilters({
    defaultAddress,
    defaultSubject,
  });
  const { criteria, setCriteria, debounce: debounceSort } = useSearchSort();
  const { formatMessage } = useIntl();

  const handleFetchAnnounces = () => {
    handleSendEventGTM('search_form', {
      user_id: authentication?.authData?.id,
      subject,
      filters: debounceFilters,
    });
  };
  const {
    announces,
    fetchMore,
    hasMore,
    isFetching,
    isLoading,
    totalCount,
    currentPage,
  } = useFetchAnnounces({
    filters: debounceFilters,
    sort: debounceSort,
    onFetch: handleFetchAnnounces,
  });

  const handleSortChange = (value: number) => {
    handleSendEventGTM('sort_by', {
      user_id: authentication?.authData?.id,
      filter_value: formattedSortCriteria[value],
    });
    setCriteria(value);
  };

  const handleFetchGetMoreAnnounces = useCallback(() => {
    handleSendEventGTM('see_more_teachers', {
      user_id: authentication?.authData?.id,
      fetched_page: currentPage + 1,
    });
    fetchMore().then();
  }, [authentication?.authData?.id, fetchMore, currentPage]);

  return (
    <>
      <Head>
        <title>
          🎓 Trouvez le Professeur particulier parfait - Les Sherpas
        </title>
        <meta
          name="description"
          content="Soutien scolaire sur mesure ✓ Professeurs certifiés & diplômés de HEC, Centrale, Sciences Po, Mines, ENS... ✓ Collège, Lycée, Prépa, Fac ✓ Dès 15€/h"
        />
        <link rel="canonical" href={`${baseUrl}professeurs`} />
      </Head>
      <SearchHeader
        subject={subject}
        setSubject={setSubject}
        address={address}
        setAddress={setAddress}
        subjects={subjects}
      />
      <div className={classes.content}>
        <PaddingContainer withMaxWidth>
          <div className={classes.mainContainer}>
            <div className={classes.sidebar}>
              <FiltersSection
                isSticky={isStickyFilters}
                sidePanelFilters={sidePanelFilters}
                setSidePanelFilters={setSidePanelFilters}
              />
            </div>
            <div className={classes.results}>
              <SponsoringPromoBanner />
              <DCEDiscountedPromoBanner />
              <SortSection
                searchResultsTotalCount={totalCount}
                isLoading={isLoading}
                isMobile={isMobile}
                criteria={criteria}
                onSortCriteriaChange={handleSortChange}
              />
              <B2BBanner />
              <SearchResultGridItems
                isLoading={isFetching || isLoading}
                announces={announces}
              />
              {hasMore && (
                <div className={classes.centerShowMoreTeachers}>
                  <Button
                    color="brand"
                    variant="primary"
                    onClick={handleFetchGetMoreAnnounces}
                    label={formatMessage(messages.showMoreTeachers)}
                  />
                </div>
              )}
            </div>
          </div>
        </PaddingContainer>
      </div>

      {!isPageWithParams && (
        <div className={classes.footer}>
          <PaddingContainer withMaxWidth>
            <SearchSeoText />
          </PaddingContainer>
        </div>
      )}
    </>
  );
};

export default Search;
